import {
  PRESENTATION_CUSTOMER_MOBILE,
  PRESENTATION_CUSTOMER_NAME,
  PRESENTATION_CUSTOMER_GENDER,
  PRESENTATION_FRANCHISE_CODE,
  PRESENTATION_FRANCHISE_USERNAME,
  PRESENTATION_HUB_PATH,
  PRESENTATION_REELS_STARTED_SERVICE_NAME,
} from './presentationConfig';
import {
  createPartnerCheckinQueue,
  fetchActiveVisit,
  startServiceCheckinQueue,
} from '../salon-admin/utils/checkinQueueApi';
import { guestHasName, hasStartedService } from '../video/utils/reelEligibleGuests';
import { handoffFromCheckin, persistSelectedServicesForRecommendations } from '../salon/utils/visitHandoff';
import {
  clearSalonFaceSession,
  getSalonEntrySource,
  markSalonFaceCheckedIn,
  readVisitHandoff,
  setActiveCheckinSession,
  writeVisitHandoff,
} from '../salon/utils/salonFaceSession';
import {
  clearSalonGuestPii,
  getSalonGuestPii,
  getSalonLastCustomerSnapshot,
  setSalonGuestPii,
  setSalonLastCustomerSnapshot,
} from '../salon/utils/salonGuestPiiStore';
import { getFranchiseOrganizationId, readFranchiseFlowAuth } from '../shared/franchiseFlowAuth';
import {
  faceIdentityHomePath,
  faceIdentityRoute,
  PASSPORT_BASE,
} from '../face-identity/utils/faceIdentityRoutes';
import { SALON_HUB_PATH, SALON_TEST_HUB_PATH } from '../salon/config/salonApp';

const PRESENTATION_FLAG = 'naturals_presentation_mode_v1';
const CHECKIN_CACHE_KEY = 'naturals_presentation_checkin_id';
const PRESENTATION_ROUTE_PREFIX = '/nyafaz-ep/presentation';

let memoryCheckin = null;

function normFranchiseKey(value) {
  return String(value || '').trim().toLowerCase();
}

function normMobile(value) {
  return String(value || '').replace(/\D/g, '');
}

function normalizePathname(pathname = '') {
  const p = String(pathname || '').trim();
  if (!p) return '/';
  return p.replace(/\/+$/, '') || '/';
}

function currentPathname() {
  if (typeof window === 'undefined') return '';
  return normalizePathname(window.location.pathname);
}

/** URL under `/nyafaz-ep/presentation` (hub + login). */
export function isPresentationRoutePath(pathname = currentPathname()) {
  const p = normalizePathname(pathname);
  return p === PRESENTATION_HUB_PATH || p.startsWith(`${PRESENTATION_ROUTE_PREFIX}/`);
}

/** Live salon experience picker — never treat as presentation demo. */
export function isLiveSalonExperienceHubPath(pathname = currentPathname()) {
  const p = normalizePathname(pathname);
  return p === SALON_HUB_PATH || p === SALON_TEST_HUB_PATH;
}

/** True when kiosk session belongs to the presentation outlet (see presentationConfig), not another franchise. */
export function isPresentationFranchiseSession(auth) {
  if (!auth || typeof auth !== 'object') return false;
  const wantUser = normFranchiseKey(PRESENTATION_FRANCHISE_USERNAME);
  const wantCode = normFranchiseKey(PRESENTATION_FRANCHISE_CODE);
  const user = normFranchiseKey(auth.username);
  const code = normFranchiseKey(auth.franchise_code);
  if (wantUser && user === wantUser) return true;
  if (wantCode && code === wantCode) return true;
  return false;
}

function hasPresentationSessionFlag() {
  try {
    return sessionStorage.getItem(PRESENTATION_FLAG) === '1';
  } catch {
    return false;
  }
}

function presentationStateFlags(state) {
  const s = state && typeof state === 'object' ? state : {};
  return Boolean(s.passportPresentation || s.fromPresentationHub || s.presentationMode);
}

/**
 * Presentation demo UX (hub redirect, recommendation overrides).
 * Session flag alone does not apply on live salon hub paths.
 */
export function isPresentationMode(locationState = null) {
  if (isLiveSalonExperienceHubPath()) return false;
  if (isPresentationRoutePath()) return true;
  if (presentationStateFlags(locationState)) return true;
  return hasPresentationSessionFlag();
}

/** Router state or session flag — user entered from presentation hub / passport tile. */
export function isPresentationNavContext(state) {
  const s = state && typeof state === 'object' ? state : {};
  if (presentationStateFlags(s)) return true;
  if (isPresentationRoutePath()) return true;
  if (isLiveSalonExperienceHubPath()) return false;
  return hasPresentationSessionFlag();
}

function isPresentationDemoGuestProfile() {
  const handoff = readVisitHandoff();
  if (handoff?.entrySource === 'presentation_demo') return true;
  if (getSalonEntrySource() === 'presentation_demo') return true;
  const demoName = String(PRESENTATION_CUSTOMER_NAME || '').trim().toLowerCase();
  const demoMobile = normMobile(PRESENTATION_CUSTOMER_MOBILE);
  const pii = getSalonGuestPii();
  const snap = getSalonLastCustomerSnapshot() || {};
  const name = String(
    handoff?.customerName || pii.name || snap.name || '',
  )
    .trim()
    .toLowerCase();
  const mobile = normMobile(handoff?.mobile || pii.mobile || snap.mobile);
  return Boolean(demoName && demoMobile && name === demoName && mobile === demoMobile);
}

/** Drop presentation demo guest/check-in when returning to live salon hub login or hub. */
export function clearPresentationDemoGuestState() {
  if (!isPresentationDemoGuestProfile()) return false;
  clearPresentationMode();
  clearSalonFaceSession();
  clearSalonGuestPii();
  return true;
}

/** Home target for kiosk flows launched from presentation (else default face-identity home). */
export function resolvePresentationAwareHomePath(pathname = '', state = null) {
  if (isPresentationNavContext(state)) return PRESENTATION_HUB_PATH;
  return faceIdentityHomePath(pathname);
}

export function setPresentationMode(active = true) {
  try {
    sessionStorage.setItem(PRESENTATION_FLAG, active ? '1' : '0');
  } catch {
    /* ignore */
  }
}

export function clearPresentationMode() {
  memoryCheckin = null;
  try {
    sessionStorage.removeItem(PRESENTATION_FLAG);
    sessionStorage.removeItem(CHECKIN_CACHE_KEY);
  } catch {
    /* ignore */
  }
}

function rememberCheckin(checkin) {
  if (!checkin?.id) return;
  memoryCheckin = checkin;
  try {
    sessionStorage.setItem(CHECKIN_CACHE_KEY, String(checkin.id));
  } catch {
    /* ignore */
  }
}

function readCachedCheckinId() {
  try {
    return String(sessionStorage.getItem(CHECKIN_CACHE_KEY) || '').trim();
  } catch {
    return '';
  }
}

function presentationDemoServiceLines({ inProgress = false } = {}) {
  const name = PRESENTATION_REELS_STARTED_SERVICE_NAME;
  return [
    {
      name,
      service_name: name,
      service_status: inProgress ? 'in_progress' : 'pending',
    },
  ];
}

function seedLocalGuestFromCheckin(checkin) {
  const handoff = handoffFromCheckin(checkin, {
    entrySource: 'presentation_demo',
    customerName: PRESENTATION_CUSTOMER_NAME,
    mobile: PRESENTATION_CUSTOMER_MOBILE,
    gender: PRESENTATION_CUSTOMER_GENDER,
  });
  if (handoff) writeVisitHandoff(handoff);
  setActiveCheckinSession({
    id: checkin.id,
    code: checkin.customer_code || '',
    status: checkin.status || 'in_service',
    faceUserId: checkin.face_user_id || '',
  });
  markSalonFaceCheckedIn({
    name: PRESENTATION_CUSTOMER_NAME,
    mobile: PRESENTATION_CUSTOMER_MOBILE,
    organizationId: getFranchiseOrganizationId(),
  });
  setSalonGuestPii({
    name: PRESENTATION_CUSTOMER_NAME,
    mobile: PRESENTATION_CUSTOMER_MOBILE,
    gender: PRESENTATION_CUSTOMER_GENDER,
  });
  setSalonLastCustomerSnapshot({
    name: PRESENTATION_CUSTOMER_NAME,
    mobile: PRESENTATION_CUSTOMER_MOBILE,
    checkinId: checkin.id,
    gender: PRESENTATION_CUSTOMER_GENDER,
  });
  const services = Array.isArray(checkin.selected_services) ? checkin.selected_services : [];
  if (services.length) {
    persistSelectedServicesForRecommendations(services);
  }
}

/**
 * Create (or reuse) a started-service check-in for demo guest — products, treatments, reels.
 */
export async function ensurePresentationGuestCheckin({ forceNew = false } = {}) {
  setPresentationMode(true);
  if (!forceNew && memoryCheckin?.id) return memoryCheckin;

  const cachedId = !forceNew ? readCachedCheckinId() : '';
  if (!forceNew && cachedId && memoryCheckin?.id === cachedId) {
    return memoryCheckin;
  }

  const checkin = await createPartnerCheckinQueue({
    customerName: PRESENTATION_CUSTOMER_NAME,
    mobile: PRESENTATION_CUSTOMER_MOBILE,
    startService: false,
    source: 'presentation_demo',
    selectedServices: presentationDemoServiceLines({ inProgress: false }),
    forceNew: Boolean(forceNew),
  });
  rememberCheckin(checkin);
  seedLocalGuestFromCheckin(checkin);
  return checkin;
}

/**
 * Reels require the same desk rule as live: guest name + at least one started service line.
 */
export async function ensurePresentationReelsCheckin() {
  setPresentationMode(true);
  let checkin = await createPartnerCheckinQueue({
    customerName: PRESENTATION_CUSTOMER_NAME,
    mobile: PRESENTATION_CUSTOMER_MOBILE,
    startService: false,
    source: 'presentation_demo',
    selectedServices: presentationDemoServiceLines({ inProgress: false }),
    forceNew: true,
  });
  if (!checkin?.id) {
    throw new Error('Could not create demo check-in for reels.');
  }

  checkin = await startServiceCheckinQueue(checkin.id, {
    selectedServices: presentationDemoServiceLines({ inProgress: true }),
  });

  const refreshed =
    (await fetchActiveVisit({ checkinId: checkin.id, includeClosed: true })) || checkin;

  if (!guestHasName(refreshed) || !hasStartedService(refreshed)) {
    throw new Error(
      'Reels need a guest with a started service on the desk. Start the demo service and try again.',
    );
  }

  rememberCheckin(refreshed);
  seedLocalGuestFromCheckin(refreshed);
  return refreshed;
}

export function buildPresentationNavState(extra = {}) {
  const checkin = memoryCheckin;
  return {
    presentationMode: true,
    customerName: PRESENTATION_CUSTOMER_NAME,
    mobile: PRESENTATION_CUSTOMER_MOBILE,
    mobileNumber: PRESENTATION_CUSTOMER_MOBILE,
    gender: PRESENTATION_CUSTOMER_GENDER,
    userData: {
      name: PRESENTATION_CUSTOMER_NAME,
      mobileNumber: PRESENTATION_CUSTOMER_MOBILE,
      gender: PRESENTATION_CUSTOMER_GENDER,
    },
    checkinId: checkin?.id || readCachedCheckinId() || null,
    fromPresentationHub: true,
    ...extra,
  };
}

export function presentationCheckinForReels() {
  const checkin = memoryCheckin;
  if (!checkin?.id) return null;
  return {
    id: checkin.id,
    customer_name: checkin.customer_name || PRESENTATION_CUSTOMER_NAME,
    customer_code: checkin.customer_code || '',
    mobile: checkin.mobile || PRESENTATION_CUSTOMER_MOBILE,
    selected_services: checkin.selected_services || [],
  };
}

/** Passport kiosk scan — same franchise device session as salon/reels (not guest JWT). */
export function openPresentationPassportScan(navigate) {
  const auth = readFranchiseFlowAuth();
  if (!auth?.organization_id) {
    throw new Error('Sign in to the franchise device before opening Passport scan.');
  }
  navigate(faceIdentityRoute('scan', PASSPORT_BASE), {
    state: buildPresentationNavState({
      passportPresentation: true,
      fromPresentationHub: true,
    }),
  });
}
